import { useEffect, useState } from 'react';
import { AcqHeader } from './components/acquisition/AcqHeader';
import { AcqHero } from './components/acquisition/AcqHero';
import { ProofTraction } from './components/acquisition/ProofTraction';
import { ScenariosSection } from './components/acquisition/ScenariosSection';
import { DiscountComparator } from './components/acquisition/DiscountComparator';
import { SensitivitySection } from './components/acquisition/SensitivitySection';
import { RisksMatrix } from './components/acquisition/RisksMatrix';
import { NinetyDayPlan } from './components/acquisition/NinetyDayPlan';
import { TwelveMonthPlan } from './components/acquisition/TwelveMonthPlan';
import { AssumptionsPanel } from './components/acquisition/AssumptionsPanel';
import { AcqFooter } from './components/acquisition/AcqFooter';
import { useAcquisition } from './hooks/useAcquisition';
import { useProject } from './hooks/useProject';
import { PlanEditor } from './components/pitch/PlanEditor';
import { SaveDialog } from './components/pitch/Dialogs';
import { CompareDialog } from './components/pitch/CompareDialog';
import { smoothScrollToId } from './lib/scrollWithOffset';
import './acquisition.css';

export default function DossierApp({ preselectHint }) {
  const [applyDiscount, setApplyDiscount] = useState(false);
  const { payload, snapshotHint, busy, error, reload } = useAcquisition(applyDiscount);

  // Unified plan editor — shared with Apresentação
  const project = useProject();
  const [planModal, setPlanModal] = useState('');
  const [editorTab, setEditorTab] = useState('products');
  const openEditor = (tab = 'products') => { setEditorTab(typeof tab === 'string' ? tab : 'products'); setPlanModal('editor'); };

  const [active, setActive] = useState('top');

  useEffect(() => {
    document.documentElement.lang = 'pt-PT';
  }, []);

  const effectiveHint = snapshotHint || preselectHint;

  useEffect(() => {
    if (effectiveHint && payload) {
      if (document.getElementById('cenarios')) {
        setTimeout(() => smoothScrollToId('cenarios'), 300);
      }
    }
  }, [effectiveHint, payload]);

  useEffect(() => {
    const onAnchorClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      const id = decodeURIComponent(href.slice(1));
      if (!document.getElementById(id)) return;
      e.preventDefault();
      smoothScrollToId(id);
      if (window.history.replaceState) window.history.replaceState(null, '', href);
    };
    document.addEventListener('click', onAnchorClick);
    return () => document.removeEventListener('click', onAnchorClick);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-25% 0px -55% 0px' },
    );
    document.querySelectorAll('section[id], header[id]').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [payload]);

  if (busy && !payload) {
    return (
      <div className="acq-shell" style={{ padding: '5rem 0' }}>
        <div className="acq-container">
          <p style={{ color: 'var(--muted)' }}>A carregar o planeamento...</p>
        </div>
      </div>
    );
  }

  if (error && !payload) {
    return (
      <div className="acq-shell" style={{ padding: '5rem 0' }}>
        <div className="acq-container">
          <div className="alert alert-warn">
            <div>
              {error}
              <button onClick={reload} className="btn btn-sm btn-ghost" style={{ marginLeft: '.75rem' }}>Tentar novamente</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const config = payload?.config;
  const scenarios = payload?.scenarios;
  const scenariosWithWC = payload?.scenarios_with_working_capital;
  const scenariosNoDiscount = payload?.scenarios_no_discount;
  const scenariosWithDiscount = payload?.scenarios_with_discount;
  const scrollTo = (id) => smoothScrollToId(id);

  return (
    <div className="acq-shell">
      <AcqHeader
        active={active}
        onEdit={openEditor}
      />
      <main>
        <AcqHero
          revealPrivate={true}
          conservative={scenarios?.conservative}
          optimistic={scenarios?.optimistic_moderate}
          price={config?.acquisition_price_eur}
          onScenarios={() => scrollTo('cenarios')}
        />
        <ProofTraction traction={config?.traction} />
        <ScenariosSection
          revealPrivate={true}
          scenarios={scenarios}
          scenariosWithWC={scenariosWithWC}
          scenariosNoDiscount={scenariosNoDiscount}
          scenariosWithDiscount={scenariosWithDiscount}
          price={config?.acquisition_price_eur}
          wcAddition={config?.working_capital_addition_eur}
          preselectHint={preselectHint}
          applyDiscount={applyDiscount}
          onApplyDiscountChange={setApplyDiscount}
        />
        <DiscountComparator
          scenarios={scenarios}
          scenariosNoDiscount={scenariosNoDiscount}
          scenariosWithDiscount={scenariosWithDiscount}
        />
        <SensitivitySection scenarioKey="optimistic_moderate" revealPrivate={true} />
        <AssumptionsPanel config={config} onEdit={() => openEditor('costs')} />
        <RisksMatrix risks={config?.risks} />
        <NinetyDayPlan plan={config?.ninety_day_plan} />
        <TwelveMonthPlan months={config?.twelve_month_plan} />
      </main>
      <AcqFooter config={config} />

      {/* Unified plan editor — same editor as Apresentação */}
      <PlanEditor
        open={planModal === 'editor'}
        onOpenChange={(o) => !o && setPlanModal('')}
        initialTab={editorTab}
        {...project}
        onSave={() => setPlanModal('save')}
        onCompare={() => setPlanModal('compare')}
        onPrint={() => {}}
      />
      <SaveDialog
        open={planModal === 'save'}
        onOpenChange={(o) => !o && setPlanModal('')}
        scenario={project.scenario}
        plan={project.plan}
        onSaved={project.replacePlan}
      />
      <CompareDialog
        open={planModal === 'compare'}
        onOpenChange={(o) => !o && setPlanModal('')}
        plan={project.plan}
        result={project.result}
        onSave={() => setPlanModal('save')}
      />

      {/* Acquisition-specific pressupostos (investment price, scenarios, growth rates) */}
    </div>
  );
}
